// src/context/VideoFavoritesContext.js
import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { app } from '../components/firebase';
import { videoFavoritesAPI } from '../services/videoFavoritesAPI';
import { toast } from 'react-hot-toast';

const VideoFavoritesContext = createContext(null);

export const VideoFavoritesProvider = ({ children }) => {
  const [favoriteIds, setFavoriteIds] = useState(new Set());
  const [pendingIds, setPendingIds] = useState(new Set());
  const [currentUser, setCurrentUser] = useState(null);

 
  useEffect(() => {
    const auth = getAuth(app);
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (user) {
        try {
          const courses = await videoFavoritesAPI.getFavoriteCourses(user.uid);
          const ids = new Set(
            (courses || []).map((c) => c.courseId || c.id).filter(Boolean)
          );
          setFavoriteIds(ids);
        } catch (err) {
          console.error('VideoFavoritesContext: failed to load favorites', err);
        }
      } else {
        setFavoriteIds(new Set());
      }
    });
    return () => unsubscribe();
  }, []);

  const isFavorite = useCallback(
    (courseId) => favoriteIds.has(courseId),
    [favoriteIds]
  );

  const isPending = useCallback(
    (courseId) => pendingIds.has(courseId),
    [pendingIds]
  );


  const toggleFavorite = useCallback(
    async (courseId, e) => {
      if (e) e.stopPropagation();
      if (!currentUser) {
        toast.error('Please log in to save favorites.');
        return;
      }
      if (pendingIds.has(courseId)) return; 

      const wasAlreadyFavorite = favoriteIds.has(courseId);

  
      setFavoriteIds((prev) => {
        const next = new Set(prev);
        if (wasAlreadyFavorite) {
          next.delete(courseId);
        } else {
          next.add(courseId);
        }
        return next;
      });

      // Mark as pending
      setPendingIds((prev) => new Set(prev).add(courseId));

      try {
        if (wasAlreadyFavorite) {
          await videoFavoritesAPI.unsaveFavorite(currentUser.uid, courseId);
          toast.success('Removed from Favorites.');
        } else {
          await videoFavoritesAPI.saveFavorite(currentUser.uid, courseId);
          toast.success('Added to Favorites.');
        }
      } catch (err) {
       
        console.error('toggleFavorite error:', err);
        setFavoriteIds((prev) => {
          const reverted = new Set(prev);
          if (wasAlreadyFavorite) {
            reverted.add(courseId);
          } else {
            reverted.delete(courseId);
          }
          return reverted;
        });
        toast.error('Failed to update favorite. Please try again.');
      } finally {
        setPendingIds((prev) => {
          const next = new Set(prev);
          next.delete(courseId);
          return next;
        });
      }
    },
    [currentUser, favoriteIds, pendingIds]
  );

  
  const removeFavoriteLocally = useCallback((courseId) => {
    setFavoriteIds((prev) => {
      const next = new Set(prev);
      next.delete(courseId);
      return next;
    });
  }, []);

  return (
    <VideoFavoritesContext.Provider
      value={{
        favoriteIds,
        isFavorite,
        isPending,
        toggleFavorite,
        removeFavoriteLocally,
        currentUser,
      }}
    >
      {children}
    </VideoFavoritesContext.Provider>
  );
};

export const useVideoFavorites = () => {
  const ctx = useContext(VideoFavoritesContext);
  if (!ctx) {
    throw new Error('useVideoFavorites must be used inside <VideoFavoritesProvider>');
  }
  return ctx;
};

export default VideoFavoritesContext;
